/**
 * @file Shared billing/subscription strings — imported by both web and mobile.
 *
 * All labels for the billing page, upgrade prompts, referral dashboard, and
 * usage warnings live here so changes propagate to both platforms.
 *
 * Usage:
 *   import { BILLING } from '../../shared/billing-strings';   // web
 *   import { BILLING } from '../shared/billing-strings';      // mobile
 */

export const BILLING = {
  // Page
  PAGE_TITLE: 'Mi Plan',
  CURRENT_PLAN: 'Plan actual',
  PLAN_FREE: 'Gratis',
  PLAN_PRO: 'Pro',
  PLAN_TRIAL: 'Prueba Pro',
  PLAN_PRO_PRICE: '$399 MXN/mes',

  // Usage
  USAGE_TITLE: 'Uso este mes',
  USAGE_APPOINTMENTS: 'citas utilizadas',
  APPOINTMENTS_USED: (current, limit) => `${current} de ${limit} citas`,
  APPOINTMENTS_UNLIMITED: 'Citas ilimitadas',
  LOCATIONS_USED: (current, limit) => `${current} de ${limit} ubicación`,
  LOCATIONS_UNLIMITED: 'Ubicaciones ilimitadas',
  USAGE_WARNING: 'Has alcanzado el límite de citas gratuitas este mes.',

  TRIAL_USAGE_LABEL: 'Prueba Pro',
  GRATIS_USAGE_LABEL: 'Plan Gratis',
  USAGE_RESETS: (date) => `Se reinicia el ${date}`,

  // Trial
  TRIAL_REMAINING: (n) => `${n} citas restantes`,
  TRIAL_ACTIVATE: 'Prueba Pro gratis',
  TRIAL_ACTIVATE_DESC: 'Sin tarjeta, sin compromiso',
  TRIAL_ACTIVATE_PERKS: ['15 citas con WhatsApp', '2 ubicaciones', 'Sin promoción de Consultto en tu perfil'],
  TRIAL_FEATURES: [
    '30 citas por mes + 15 con WhatsApp',
    '2 ubicaciones',
    'Recordatorios por email',
    'Perfil público',
    'Calendario y agenda',
    'Gestión de pacientes',
    'Listado de servicios',
    'Horarios personalizables',
    'Estadísticas y dashboard',
    'Personalización de perfil',
    'Sin promoción de Consultto en tu perfil',
  ],
  TRIAL_SUCCESS_TITLE: '¡Bienvenido a Pro!',
  TRIAL_SUCCESS_SUBTITLE: 'Tu prueba gratuita está activa',
  TRIAL_SUCCESS_SHARE: 'Compartir mi perfil',
  TRIAL_SUCCESS_COMPLETE_PROFILE: 'Completar mi perfil',
  TRIAL_SUCCESS_CLOSE: 'Cerrar',
  TRIAL_SUCCESS_LINK_COPIED: 'Enlace copiado',

  // Subscription success (Pro)
  SUBSCRIPTION_SUCCESS_TITLE_PRO: '¡Ya eres Pro!',
  SUBSCRIPTION_SUCCESS_SUBTITLE: 'Tu suscripción está activa',

  TRIAL_EXPIRED: 'Tu prueba Pro terminó',
  TRIAL_EXPIRED_DESC: 'Suscríbete para seguir con citas ilimitadas y WhatsApp',

  // Upgrade
  UPGRADE_TITLE: 'Desbloquea todo con Pro',
  UPGRADE_SUBTITLE: 'Desbloquea todo el potencial de Consultto',
  UPGRADE_CTA: 'Actualizar a Pro',
  UPGRADE_CTA_FULL: 'Suscríbete a Pro — $399/mes',
  LAUNCH_PROMO_NOTE: 'Precio de lanzamiento — tu primer año',
  LAUNCH_ORIGINAL_PRICE: '$999',
  UPGRADE_ERROR: 'Error al iniciar el proceso de pago.',
  UPGRADE_FEATURES: [
    'Citas ilimitadas',
    'Ubicaciones ilimitadas',
    'Notificaciones por WhatsApp a tus pacientes',
    'Recordatorios por email',
    'Perfil público',
    'Calendario y agenda',
    'Gestión de pacientes',
    'Listado de servicios',
    'Horarios personalizables',
    'Estadísticas y dashboard',
    'Personalización de perfil',
    'Soporte prioritario',
    'Sin promoción de Consultto en tu perfil',
  ],
  UPGRADE_HIGHLIGHTS: [
    'Citas ilimitadas',
    'Ubicaciones ilimitadas',
    'Notificaciones por WhatsApp a tus pacientes',
    'Sin promoción de Consultto en tu perfil',
  ],
  UPGRADE_INCLUDES_GRATIS: 'Incluye todo el plan Gratis',

  GRATIS_FEATURES: [
    '30 citas por mes',
    '1 ubicación',
    'Recordatorios por email',
    'Perfil público',
    'Calendario y agenda',
    'Gestión de pacientes',
    'Listado de servicios',
    'Horarios personalizables',
    'Estadísticas y dashboard',
    'Personalización de perfil',
    'Promoción de Consultto en tu perfil',
  ],

  // Manage
  MANAGE_SUBSCRIPTION: 'Administrar suscripción',
  PORTAL_ERROR: 'Error al abrir el portal de suscripción.',
  PLAN_EXPIRES: 'Vigente hasta',
  PLAN_RENEWS: 'Se renueva el',
  PLAN_CANCELLING: 'Se cancela el',
  PLAN_ACTIVE: 'Activo',
  PLAN_STATUS_ACTIVE: 'Activa',
  PLAN_STATUS_CANCELLING: 'Cancelándose',
  PLAN_STATUS_CANCELLED: 'Cancelada',
  PLAN_STATUS_PAST_DUE: 'Pago pendiente',
  PLAN_CANCELLED_MESSAGE: 'Tu suscripción ha finalizado',

  // Referral
  REFERRAL_TITLE: 'Programa de Referidos',
  REFERRAL_SUBTITLE: 'Invita a un colega y gana 1 mes gratis de Pro.',
  REFERRAL_CODE: 'Tu código de referido',
  REFERRAL_LINK: 'Comparte este enlace',
  REFERRAL_COPY: 'Copiar enlace',
  REFERRAL_COPIED: 'Enlace copiado',
  REFERRAL_TOTAL: 'Total referidos',
  REFERRAL_COMPLETED: 'Completados',
  REFERRAL_PENDING: 'Pendientes',
  REFERRAL_MONTHS: 'Meses ganados',
  REFERRAL_EMPTY: 'Aún no has referido a nadie',
  REFERRAL_PENDING_NOTE: 'Se activa cuando complete su primer pago',
  REFERRAL_SHARE: 'Compartir',

  // Usage warnings (shown in dashboard/appointment creation)
  USAGE_WARNING_10: (limit) => `Has usado 10 de tus ${limit} citas gratuitas este mes`,
  USAGE_WARNING_13: (limit) => `¡Solo te quedan ${limit - 13} citas gratuitas este mes!`,
  USAGE_WARNING_LIMIT: 'Has alcanzado el límite de citas gratuitas este mes',
  USAGE_UPGRADE_PROMPT: 'Actualiza a Pro para citas ilimitadas',

  // Fiscal data (CFDI invoicing)
  FISCAL_TITLE: 'Datos de facturación',
  FISCAL_QUESTION: '¿Necesitas factura?',
  FISCAL_SUBTITLE: 'Proporciona tus datos fiscales para que podamos emitir tus facturas (CFDI).',
  FISCAL_RFC: 'RFC',
  FISCAL_RFC_HELPER: '12 o 13 caracteres, como aparece en tu constancia del SAT',
  FISCAL_RAZON_SOCIAL: 'Razón social',
  FISCAL_RAZON_SOCIAL_HELPER: 'Nombre o denominación tal como aparece en tu constancia',
  FISCAL_REGIMEN: 'Régimen fiscal',
  FISCAL_USO_CFDI: 'Uso de CFDI',
  FISCAL_CP: 'Código postal fiscal',
  FISCAL_CP_HELPER: '5 dígitos, como aparece en tu constancia',
  FISCAL_CONSTANCIA: 'Constancia de Situación Fiscal',
  FISCAL_CONSTANCIA_HELPER: 'Sube tu constancia del SAT en PDF para verificar tus datos',
  FISCAL_CONSTANCIA_UPLOADED: 'Constancia subida',
  FISCAL_SAVE: 'Guardar datos fiscales',
  FISCAL_SAVED: 'Datos fiscales guardados',
  FISCAL_SAVE_ERROR: 'Error al guardar datos fiscales',
  FISCAL_CONSTANCIA_ERROR: 'Error al subir constancia',
  FISCAL_CONSTANCIA_SELECTED: 'PDF seleccionado',
  FISCAL_CONSTANCIA_SELECT: 'Seleccionar PDF',
  FISCAL_REGIMEN_PLACEHOLDER: 'Seleccionar régimen',
  FISCAL_USO_CFDI_PLACEHOLDER: 'Seleccionar uso',
  FISCAL_RAZON_SOCIAL_PLACEHOLDER: 'Nombre o denominación',
  FISCAL_RFC_PLACEHOLDER: 'GARC850101ABC',
  FISCAL_CP_PLACEHOLDER: '06600',

  // Billing UI
  ACTIVE_FEATURES: 'Funciones activas',
  LOAD_ERROR: 'No se pudo cargar el estado de tu plan.',
  TRIAL_ACTIVATE_ERROR: 'No se pudo activar la prueba.',
  REFERRAL_SHARE_MESSAGE: (link) => `Regístrate en Consultto y obtén tu perfil profesional gratis: ${link}`,
  BACK: 'Volver',
  CLOSE: 'Cerrar',

  // Public profile banner (shown on free-tier profiles)
  PUBLIC_PROFILE_BANNER: 'Recibe citas en línea',
  PUBLIC_PROFILE_BANNER_CTA: 'Empieza gratis',
  PUBLIC_PROFILE_BANNER_URL: '/registro',
};
